import React from 'react';
import {useTranslation} from 'react-i18next';

import {endOfDay} from 'date-fns/endOfDay';
import {startOfMonth} from 'date-fns/startOfMonth';
import {subYears} from 'date-fns/subYears';
import isObject from 'lodash/isObject';

import {useDayCycle} from '@edna/hooks';
import {getPeriod} from '@edna/utils';

import {TCalendarDate, isRangeValue} from '../Calendar';
import Dropdown from '../Dropdown';
import {formField} from '../FormField';
import * as S from './style';
import {DEFAULT_OPTIONS, THandler, TOption, TValue} from './types';
import {compareOptionWithValue, getValue, renderRangeValue} from './utils';

export type TProps = {
  className?: string;
  showInput?: boolean;
  showLabel?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  value?: any;
  options?: TOption[];
  onChange?: THandler;
  showHeaderDate?: boolean;
  needForceClosing?: boolean;
  minDate?: Date | null;
};

const PeriodPicker = React.memo<TProps>(
  ({
    className,
    showInput = true,
    showLabel = true,
    disabled,
    readOnly,
    value: propValue,
    options = DEFAULT_OPTIONS,
    onChange,
    showHeaderDate,
    minDate: propMinDate,
    needForceClosing,
  }) => {
    const {t} = useTranslation();

    const [value, setValue] = React.useState<TValue>(getValue(propValue));
    const [isOpen, setIsOpen] = React.useState(false);
    const closeButtonRef = React.useRef<HTMLButtonElement>(null);

    React.useEffect(() => {
      if (!isOpen) {
        setValue(getValue(propValue));
      }
    }, [isOpen, propValue]);

    const handleOpen = React.useCallback(() => setIsOpen(true), []);
    const handleClose = React.useCallback(() => setIsOpen(false), []);

    React.useEffect(() => {
      if (!isOpen && needForceClosing) {
        closeButtonRef.current?.click();
      }
    }, [isOpen]);

    const now = useDayCycle();

    const minDate = React.useMemo(
      () => propMinDate ?? startOfMonth(subYears(now, 2)),
      [propMinDate, now],
    );

    const renderedValue = React.useMemo(() => {
      const selectedOption = options.find((option) => compareOptionWithValue(option, value));

      if (!selectedOption) {
        return '';
      }

      if (isObject(selectedOption.value)) {
        return selectedOption.label;
      }

      if (selectedOption.value === 'range') {
        return renderRangeValue(value);
      }

      const label = t([
        `EdnaPeriodPicker:period.${selectedOption.label}`,
        `PeriodPicker:period.${selectedOption.label}`,
      ]);

      if (selectedOption.value !== 'anytime' && showHeaderDate) {
        return `${label}, ${renderRangeValue(getPeriod(selectedOption.value))}`;
      }

      return label;
    }, [options, showHeaderDate, t, value]);

    const handleChange = React.useCallback(
      (nextValue: TValue) => {
        onChange?.(nextValue);

        if (propValue === undefined) {
          setValue(nextValue);
        }
      },
      [propValue, onChange],
    );

    const handleSubmitDate = React.useCallback(
      (nextDate: TCalendarDate) => {
        if (isRangeValue(nextDate) && nextDate.startDate) {
          handleChange({
            period: 'range',
            startDate: nextDate.startDate,
            stopDate: nextDate.endDate || endOfDay(nextDate.startDate),
          });
        }

        handleClose();
      },
      [handleChange, handleClose],
    );

    const handleOptionClick = React.useCallback(
      (option: TOption) => {
        if (option.value === 'range') {
          handleOpen();

          return;
        }

        handleChange(isObject(option.value) ? option.value : getPeriod(option.value));

        if (needForceClosing) {
          closeButtonRef.current?.click();
        }
      },
      [handleOpen, handleChange, needForceClosing],
    );

    const renderHead = React.useCallback(
      (headProps: {isOpen: boolean}) => (
        <S.PeriodPicker
          isOpen={headProps.isOpen}
          showInput={showInput}
          disabled={disabled}
          readOnly={readOnly}
        >
          <S.CalendarIcon />
          {showLabel && <S.Value>{renderedValue}</S.Value>}
          <S.ArrowIcon />
        </S.PeriodPicker>
      ),
      [disabled, readOnly, renderedValue, showInput, showLabel],
    );

    const getOptionLabel = React.useCallback(
      (option: TOption) => {
        if (isObject(option.value)) {
          return option.label;
        }
        const isRange = option.value === 'range' && value.period === 'range';

        return isRange
          ? renderRangeValue(value)
          : t([`EdnaPeriodPicker:period.${option.label}`, `PeriodPicker:period.${option.label}`]);
      },
      [t, value],
    );

    const renderOverlay = React.useCallback(() => {
      if (isOpen) {
        return (
          <S.Calendar
            calendarType="DATE_RANGE"
            value={{startDate: value.startDate, endDate: value.stopDate}}
            now={now}
            minDate={minDate}
            maxDate={now}
            onSubmit={handleSubmitDate}
            onCancel={handleClose}
            hasAcceptButtons
          />
        );
      }

      return (
        <>
          {options.map((option) => (
            <S.Option
              key={JSON.stringify(option.value)}
              selected={compareOptionWithValue(option, value)}
              value={getOptionLabel(option)}
              onClick={() => handleOptionClick(option)}
            />
          ))}
          <button type="button" name="cancel" hidden ref={closeButtonRef}>
            none
          </button>
        </>
      );
    }, [
      handleClose,
      getOptionLabel,
      handleSubmitDate,
      handleOptionClick,
      isOpen,
      minDate,
      now,
      options,
      value,
    ]);

    return (
      <Dropdown
        disabled={disabled}
        readOnly={readOnly}
        hasVerticalPaddings={!isOpen}
        hasOverlayArrow={!showLabel}
        className={className}
        head={renderHead}
        overlay={renderOverlay}
        onClose={handleClose}
        closeWhen={isOpen ? 'clickOutsideOverlayOrSubmitOrCancel' : 'clickAny'}
      />
    );
  },
);

export {PeriodPicker};

export const PeriodPickerField = formField<TProps, TValue>(PeriodPicker);
